import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ChevronRight, 
  Target, 
  Globe, 
  Share2, 
  Bot, 
  Code2, 
  LayoutDashboard, 
  MousePointerClick, 
  Check, 
  X, 
  Plus, 
  Minus, 
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Blocks,
  Infinity as InfinityIcon,
  Quote,
  LayoutTemplate
} from 'lucide-react';

const FAQItem = ({ question, answer }: { question: string, answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-white/10 py-8">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full flex items-center justify-between text-left focus:outline-none group"
      >
        <h4 className="text-xl md:text-2xl font-bold text-neutral-300 group-hover:text-white transition-colors pr-6">{question}</h4>
        <div className="flex-shrink-0 text-neutral-500 group-hover:text-purple-400 transition-colors">
          {isOpen ? <Minus size={28} /> : <Plus size={28} />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="pt-6 text-neutral-400 leading-relaxed text-lg pb-2 font-light">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const NyrohLanding: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#000000] text-white font-sans selection:bg-purple-500/30 overflow-x-hidden">
      
      {/* HEADER / NAVIGATION */}
      <header className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-5 transition-all duration-300 ${scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white flex items-center justify-center rounded">
            <span className="text-black font-black text-xl leading-none tracking-tighter">N</span>
          </div>
          <span className="font-bold text-xl tracking-widest uppercase">Nyroh</span>
        </div>
        <div className="flex items-center gap-6 text-sm font-medium">
           <a href="#/login" className="text-neutral-400 hover:text-white transition-colors hidden sm:block">Acesso Cliente</a>
           <a href="#/core" className="group flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-5 py-2.5 rounded-full transition-all">
             <span className="text-neutral-300 group-hover:text-white font-mono uppercase tracking-widest text-[10px]">Nyroh Core</span>
             <ChevronRight size={14} className="text-purple-500 group-hover:translate-x-1 transition-transform" />
           </a>
        </div>
      </header>

      {/* DOBRA 1: O Impacto Central (Hero) */}
      <section className="relative min-h-screen flex items-center justify-center pt-24 pb-12 px-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[1000px] max-h-[1000px] bg-purple-900/10 blur-[150px] rounded-full pointer-events-none" />
        
        <div className="max-w-6xl mx-auto text-center relative z-10 flex flex-col items-center mt-12">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: "easeOut" }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/10 bg-white/5 text-neutral-300 text-xs font-semibold tracking-widest uppercase mb-10 backdrop-blur-sm"
          >
            <Sparkles size={14} className="text-purple-400" /> O Hub D2C Tecnológico
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-5xl md:text-7xl lg:text-[6rem] font-black tracking-tighter mb-8 leading-[1.05]"
          >
            O fim do pedágio <br className="hidden lg:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neutral-100 via-purple-300 to-indigo-500">
              da ineficiência.
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-3xl text-neutral-400 max-w-4xl mb-16 leading-tight font-light"
          >
            A mesma excelência criativa e técnica arquitetada pelas grandes agências, fornecida a você por uma <strong className="text-white font-medium">fração absoluta do orçamento histórico.</strong>
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center w-full"
          >
            <a href="https://wa.me/5511999999999" target="_blank" rel="noreferrer" className="w-full sm:w-auto px-12 py-6 bg-white hover:bg-neutral-200 text-black font-black uppercase tracking-widest rounded-2xl transition-all shadow-[0_0_50px_rgba(255,255,255,0.15)] hover:shadow-[0_0_80px_rgba(255,255,255,0.25)] flex items-center justify-center gap-4 text-sm md:text-base group">
              Inicie o Diagnóstico Nyroh <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* DOBRA 2: A Nossa Filosofia (O Hub) */}
      <section className="py-40 px-6 bg-[#030303] border-t border-white/5 relative overflow-hidden">
        <div className="absolute left-[-20%] top-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-900/10 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
           <motion.h2 
             initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
             className="text-4xl md:text-6xl font-black tracking-tight mb-10"
           >
             A tecnologia estilhaçou o custo. <br/><span className="text-neutral-500">O mercado só escondeu isso de você.</span>
           </motion.h2>
           <motion.p 
             initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ delay: 0.1 }}
             className="text-2xl text-neutral-400 font-light leading-relaxed max-w-4xl mx-auto"
           >
             As soluções de ponta evoluíram. A Nyroh utiliza automação profunda, I.A. na base e engenharia estrutural para obliterar nossas próprias horas ociosas. 
             <strong className="text-white font-medium block mt-6">
                Nós repassamos o ganho comercial dessa escala diretamente para o seu orçamento e sua lucratividade. Chega de inflação artificial.
             </strong>
           </motion.p>
        </div>
      </section>

      {/* DOBRA 3: Os 6 Pilares Orbitando (A Visão Geral) */}
      <section className="py-24 px-6 border-t border-white/5 bg-black">
         <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="md:w-1/3">
               <h3 className="text-3xl font-bold mb-4">Um Ecossistema Único Ponto-a-Ponto.</h3>
               <p className="text-neutral-500 leading-relaxed text-lg">Sem intermediários. Orquestramos a aquisição, a infraestrutura e a retenção de ponta a ponta na sua empresa.</p>
            </div>
            <div className="md:w-2/3 flex flex-wrap justify-center gap-4">
               {[
                 { name: "Web & SEO", icon: <Globe size={18}/> },
                 { name: "Tráfego (Ads)", icon: <Target size={18}/> },
                 { name: "Social Media", icon: <Share2 size={18}/> },
                 { name: "Automação I.A", icon: <Bot size={18}/> },
                 { name: "Sistemas Otimizados", icon: <Code2 size={18}/> },
                 { name: "Dashboard e Dados", icon: <LayoutDashboard size={18}/> }
               ].map((pilar, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }} className="flex items-center gap-3 px-6 py-4 bg-white/5 border border-white/10 rounded-full font-mono text-sm tracking-widest uppercase text-neutral-300">
                    <span className="text-purple-500">{pilar.icon}</span> {pilar.name}
                  </motion.div>
               ))}
            </div>
         </div>
      </section>

      {/* DOBRAS MAIN SERVICES: Aprofundamento dos Pilares */}
      
      {/* DOBRA 4: Pilar Web & SEO */}
      <section className="py-40 px-6 relative overflow-hidden bg-[#030303]">
        <div className="absolute right-0 top-1/4 w-[500px] h-[500px] bg-purple-900/10 blur-[150px] pointer-events-none" />
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center">
           <div className="flex-1 order-2 lg:order-1 relative w-full">
              <div className="aspect-[4/3] w-full bg-[#050505] border border-white/10 rounded-3xl overflow-hidden flex flex-col p-8 shadow-[10px_10px_60px_rgba(0,0,0,0.8)] relative z-10 box-border">
                 <div className="w-full h-8 bg-white/5 rounded-t-xl mb-4 border-b border-white/5 flex gap-2 items-center px-4"><div className="w-2 h-2 rounded-full bg-red-500"/><div className="w-2 h-2 rounded-full bg-yellow-500"/><div className="w-2 h-2 rounded-full bg-green-500"/></div>
                 <div className="flex-1 bg-gradient-to-br from-white/5 to-transparent rounded-lg border border-white/10 p-6 flex flex-col justify-end relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/20 blur-3xl"></div>
                    <div className="w-2/3 h-6 bg-white/20 rounded-lg mb-4"></div>
                    <div className="w-1/2 h-4 bg-white/10 rounded-lg mb-8"></div>
                    <div className="flex gap-4">
                       <div className="flex-1 bg-white/5 rounded border border-white/5 h-24"></div>
                       <div className="flex-1 bg-white/5 rounded border border-white/5 h-24"></div>
                       <div className="flex-1 bg-white/5 rounded border border-white/5 h-24"></div>
                    </div>
                 </div>
              </div>
           </div>
           <div className="flex-1 order-1 lg:order-2">
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once:true, margin: "-100px" }}>
                <Globe className="text-purple-500 mb-8" size={56} strokeWidth={1} />
                <h2 className="text-5xl lg:text-6xl font-black mb-8 tracking-tight">Pilar Web & SEO.</h2>
                <p className="text-2xl text-neutral-400 font-light leading-relaxed mb-6">
                   Você não constrói um negócio premium sustentado em uma tenda improvisada.
                </p>
                <p className="text-lg text-neutral-500 leading-relaxed font-light">
                   Nossas vitrines são desenhadas para o topo orgânico do Google. Arquiteturas ultrarrápidas, blindadas contra lentidão e sem a carga arrastada que afunda as conversões. Um domínio soberano da sua presença visual e técnica, eliminando taxas de perda por carregamento moroso.
                </p>
              </motion.div>
           </div>
        </div>
      </section>

      {/* DOBRA 5: Pilar Ads */}
      <section className="py-40 px-6 bg-[#000000] border-y border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center">
           <div className="flex-1">
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once:true, margin: "-100px" }}>
                <Target className="text-emerald-500 mb-8" size={56} strokeWidth={1} />
                <h2 className="text-5xl lg:text-6xl font-black mb-8 tracking-tight">O Motor de Demanda (Ads).</h2>
                <p className="text-2xl text-neutral-400 font-light leading-relaxed mb-6">
                   Não caçamos cliques vãos. Injetamos verba no comportamento exato de intenção de compra do seu usuário.
                </p>
                <p className="text-lg text-neutral-500 leading-relaxed font-light">
                   Operamos a gestão pesada de Google e Meta. Sem relatórios cegos e eufemismos para dinheiro perdido: estruturamos precisão militar nas buscas por sua empresa cortando as campanhas que roubam caixa e escalando exclusivamente o que jorra receita validada para o funil.
                </p>
              </motion.div>
           </div>
           <div className="flex-1 relative w-full lg:w-auto">
              <div className="aspect-square w-full max-w-[500px] mx-auto bg-[#050505] border border-emerald-900/40 rounded-full flex items-center justify-center relative overflow-hidden shadow-[0_0_100px_rgba(16,185,129,0.1)]">
                 <div className="absolute inset-0 bg-[conic-gradient(from_0deg,transparent_0_340deg,rgba(16,185,129,0.3)_360deg)] animate-[spin_6s_linear_infinite] opacity-60"></div>
                 <div className="absolute inset-4 bg-[#020202] rounded-full border border-emerald-900/30 flex items-center justify-center flex-col z-10 box-border p-10">
                    <span className="text-emerald-500 font-mono text-sm tracking-widest uppercase mb-4">Eficiência de Orçamento</span>
                    <div className="text-6xl font-black text-white leading-none">High</div>
                    <div className="text-6xl font-black text-emerald-500 leading-none mt-2">ROAS</div>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* DOBRA 6: Pilar Social Media */}
      <section className="py-40 px-6 relative overflow-hidden bg-[#030303]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center">
           <div className="flex-1 order-2 lg:order-1 relative w-full flex gap-6 justify-center">
              <div className="w-[45%] max-w-[280px] aspect-[9/16] bg-[#0A0A0A] rounded-[3rem] border-8 border-[#111] overflow-hidden relative shadow-2xl transform -translate-y-8">
                 <div className="absolute inset-0 bg-gradient-to-b from-blue-500/20 to-transparent"></div>
                 <div className="p-4 flex gap-2 items-center"><div className="w-8 h-8 bg-white/10 rounded-full"></div><div className="h-2 w-16 bg-white/10 rounded"></div></div>
                 <div className="mt-8 mx-4 h-48 bg-white/5 rounded-xl border border-white/10"></div>
              </div>
              <div className="w-[45%] max-w-[280px] aspect-[9/16] bg-[#0A0A0A] rounded-[3rem] border-8 border-[#111] overflow-hidden relative shadow-2xl transform translate-y-8">
                 <div className="absolute inset-0 bg-gradient-to-b from-purple-500/20 to-transparent"></div>
                 <div className="p-4 flex gap-2 items-center"><div className="w-8 h-8 bg-white/10 rounded-full"></div><div className="h-2 w-16 bg-white/10 rounded"></div></div>
                 <div className="mt-4 mx-4 h-64 bg-white/5 rounded-xl border border-white/10 flex items-center justify-center"><Share2 className="text-white/20"/></div>
              </div>
           </div>
           <div className="flex-1 order-1 lg:order-2">
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once:true, margin: "-100px" }}>
                <Share2 className="text-blue-500 mb-8" size={56} strokeWidth={1} />
                <h2 className="text-5xl lg:text-6xl font-black mb-8 tracking-tight">Comando de Território (Social).</h2>
                <p className="text-2xl text-neutral-400 font-light leading-relaxed mb-6">
                   Constância estética implacável e maturidade pura. Marcas líderes de respeito não operam por vaidade.
                </p>
                <p className="text-lg text-neutral-500 leading-relaxed font-light">
                   Posicionamento é patrimônio. A Nyroh comanda frentes de Social Media orquestrando cada peça gráfica e editorial não como "um simples post num feed", mas buscando cravar a superioridade impiedosa de sua marca rumo à Autoridade Absoluta.
                </p>
              </motion.div>
           </div>
        </div>
      </section>

      {/* DOBRA 7: Pilar Automação & IA */}
      <section className="py-40 px-6 bg-[#000000] border-y border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center">
           <div className="flex-1">
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once:true, margin: "-100px" }}>
                <Bot className="text-indigo-500 mb-8" size={56} strokeWidth={1} />
                <h2 className="text-5xl lg:text-6xl font-black mb-8 tracking-tight">O Exército Silencioso.</h2>
                <p className="text-2xl text-neutral-400 font-light leading-relaxed mb-6">
                   Agentes Cognitivos I.A. operando comercial em escala real, fechando propostas ativas às 03:00 da manhã.
                </p>
                <p className="text-lg text-neutral-500 leading-relaxed font-light">
                   Você perde faturamento enquanto sua equipe dorme ou demora minutos preciosos para responder clientes aflitos. Nós implementamos assistentes avançados treinados no núcleo da sua empresa: compreendem dores de leads, contornam objeções complexas e agem com raciocínio assustadoramente humano e instantâneo.
                </p>
              </motion.div>
           </div>
           <div className="flex-1 relative w-full">
              <div className="w-full max-w-md mx-auto bg-neutral-900 border border-white/10 rounded-[2rem] p-6 shadow-2xl relative">
                 <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-10"></div>
                 <div className="flex flex-col gap-6">
                    <div className="bg-white/10 p-5 rounded-2xl rounded-bl-sm self-start max-w-[85%] text-neutral-300 font-light">
                       Eu vi o maquinário pesado operando. Viável marcarmos uma sessão presencial no meu complexo logístico aos sábados?
                    </div>
                    <div className="bg-indigo-900/30 p-5 rounded-2xl rounded-br-sm border border-indigo-500/20 self-end max-w-[85%] text-indigo-100 font-light relative">
                       Trabalhamos em flexibilidade ininterrupta. Sábado pela manhã é viável. A logística exige mapear toda a esteira do galpão antecipadamente. Qual a sua porta D2 atual para pré-análise dos auditores?
                       <div className="absolute right-3 bottom-1.5 text-[9px] text-indigo-300/40">Agora mesmo</div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* DOBRA 8: Pilar Engenharia */}
      <section className="py-40 px-6 bg-[#030303] relative overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center">
           <div className="flex-1 order-2 lg:order-1 relative w-full">
              <div className="aspect-square bg-[#050505] border border-white/10 rounded-[3rem] p-16 relative overflow-hidden flex items-center justify-center shadow-2xl box-border">
                 <div className="absolute inset-0 opacity-10 blur-sm bg-[url('https://picsum.photos/seed/codez/1000/1000')] bg-cover mix-blend-overlay"></div>
                 <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-transparent to-[#050505]"></div>
                 <Code2 className="text-orange-500/30 w-full h-full p-10 relative z-10" strokeWidth={1} />
              </div>
           </div>
           <div className="flex-1 order-1 lg:order-2">
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once:true, margin: "-100px" }}>
                <Code2 className="text-orange-400 mb-8" size={56} strokeWidth={1} />
                <h2 className="text-5xl lg:text-6xl font-black mb-8 tracking-tight">Sistemas Robutos (Code).</h2>
                <p className="text-2xl text-neutral-400 font-light leading-relaxed mb-6">
                   Onde softwares engessados de prateleira causam teto no seu negócio, nossa arquitetura customizada inicia a largada.
                </p>
                <p className="text-lg text-neutral-500 leading-relaxed font-light">
                   Projetamos e hospedamos sistemas comerciais de gestão e entrega operacional. De plataformas de uso interno complexas a aplicativos de alta demanda, estruturamos pilhas nativas focadas em escalabilidade pura, usando a metodologia direta Nyroh.
                </p>
              </motion.div>
           </div>
        </div>
      </section>

      {/* DOBRA 9 & 10: O Controle (Dashboard Nyroh) e o Live Editor */}
      <section className="pt-40 pb-20 px-6 bg-gradient-to-t from-[#0A0A0A] to-[#010101] border-y border-white/5 relative overflow-hidden">
         <div className="max-w-7xl mx-auto text-center mb-32">
            <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-8">Cockpit Central e Soberano.</h2>
            <p className="text-2xl text-neutral-400 font-light max-w-4xl mx-auto leading-relaxed">
               Métricas dispersas, dependência de envio de PDFs corporativos e dezenas de janelas para olhar sua marca. Esqueça tudo isso. Seja dono unificado em seu Dashboard.
            </p>
         </div>

         {/* Dashboard Mockup - Dobra 9 */}
         <div className="max-w-6xl mx-auto mb-32 relative">
            <div className="absolute -inset-10 bg-purple-600/10 blur-[100px] z-0 rounded-full"></div>
            <div className="w-full aspect-[16/10] bg-[#0A0A0A] border border-white/10 rounded-2xl md:rounded-[2rem] shadow-2xl relative z-10 flex flex-col overflow-hidden ring-1 ring-white/5">
                <div className="h-12 border-b border-white/10 bg-[#050505] flex items-center px-6 gap-4 py-2">
                   <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500/50"></div><div className="w-3 h-3 rounded-full bg-yellow-500/50"></div><div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                   </div>
                   <div className="bg-white/5 py-1 px-4 rounded md:w-96 text-center text-xs uppercase font-mono text-neutral-500 tracking-widest hidden md:block">app.nyroh.com / auth</div>
                </div>
                <div className="flex-1 flex overflow-hidden">
                   <div className="w-64 border-r border-white/5 bg-[#030303] hidden sm:flex flex-col p-6 gap-6">
                      <div className="h-6 w-1/2 bg-white/20 mb-4 rounded"></div>
                      <div className="h-8 w-full bg-white/5 rounded"></div>
                      <div className="h-8 w-full bg-white/5 rounded"></div>
                      <div className="h-8 w-full bg-purple-500/20 border border-purple-500/30 rounded"></div>
                      <div className="h-8 w-full bg-white/5 rounded"></div>
                   </div>
                   <div className="flex-1 bg-[#080808] p-8 flex flex-col gap-6 relative">
                      <div className="flex items-center justify-between">
                         <div className="w-1/3 h-8 bg-white/10 rounded"></div>
                         <div className="w-1/4 h-8 bg-purple-500/80 rounded-full"></div>
                      </div>
                      <div className="flex gap-6 h-48">
                         <div className="flex-[2] bg-white/5 border border-white/10 rounded-xl"></div>
                         <div className="flex-1 flex flex-col gap-6">
                            <div className="flex-1 bg-white/5 border border-white/10 rounded-xl"></div>
                            <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 rounded-xl"></div>
                         </div>
                      </div>
                      <div className="flex-1 bg-white/5 border border-white/10 rounded-xl"></div>
                   </div>
                </div>
            </div>
         </div>

         {/* Fator Independência / Live Editor - Dobra 10 */}
         <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-16 items-center">
             <div className="flex-1">
                <MousePointerClick className="text-purple-500 mb-8" size={56} strokeWidth={1} />
                <h4 className="text-4xl font-black mb-6 tracking-tight">O Live Editor Nativo.</h4>
                <p className="text-xl text-neutral-400 leading-relaxed font-light">A burocracia de cobrar manutenção cara despenca. Abra a área logada do seu site por nós desenhado, aponte, clique nas estruturas da tela final e reedite o que quiser. Textos, imagens, cores, botões. Sem pedir permissão. Sem suporte chato em sexta-feira no final do expediente.</p>
             </div>
             <div className="flex-1 w-full bg-neutral-900 border border-white/10 rounded-[2rem] p-8 relative flex items-center justify-center aspect-video overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://picsum.photos/seed/editor/1000/600')] bg-cover opacity-20 sepia contrast-150"></div>
                <div className="bg-[#050505] p-6 border border-white/20 rounded-xl shadow-[0_30px_60px_rgba(0,0,0,0.5)] z-10 w-full max-w-sm flex flex-col gap-4">
                   <div className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest bg-white/5 self-start px-2 py-1 rounded">Elemento Atualizado</div>
                   <div className="text-lg font-serif">Modificar Manchete ▊</div>
                   <div className="bg-purple-600 rounded px-4 py-2 text-center text-sm font-bold uppercase tracking-widest cursor-pointer shadow-lg mt-2">Salvar Publicação</div>
                </div>
             </div>
         </div>
      </section>

      {/* DOBRA 11: A Matriz do Desperdício */}
      <section className="py-40 px-6 bg-[#000000]">
        <div className="max-w-6xl mx-auto">
           <div className="text-center mb-24">
              <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-8">A Matriz do Desperdício Antigo.</h2>
              <p className="text-2xl text-neutral-400 max-w-4xl mx-auto font-light">
                Descubra exatamente onde evapora e sangra o seu saldo ao prosseguir com operações que fingem não viver no ano atual da eficiência.
              </p>
           </div>

           <div className="bg-[#050505] rounded-[2rem] md:rounded-[3rem] border border-white/10 shadow-[0_20px_100px_rgba(255,255,255,0.02)] overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                 {/* COLUNA TRADICIONAL */}
                 <div className="p-8 md:p-16 border-b md:border-b-0 md:border-r border-white/10">
                    <h3 className="text-2xl font-bold uppercase tracking-widest text-neutral-500 mb-10 flex items-center justify-between">O Modelo Cego <X className="text-red-900"/></h3>
                    <ul className="space-y-12">
                       <li><strong className="block text-xl text-neutral-300 font-bold mb-2">Orçamentos Fantasmas</strong><span className="text-neutral-500 font-light text-lg">Pague caro por dias inteiros bancando o tempo morto de uma equipe ociosa, inflacionando a própria ineficiência.</span></li>
                       <li><strong className="block text-xl text-neutral-300 font-bold mb-2">Prazos e Refações Intermináveis</strong><span className="text-neutral-500 font-light text-lg">Acordos onde o escopo nunca entra no ar. Desenvolvimento lento com codificação manual ultrapassada em tudo.</span></li>
                       <li><strong className="block text-xl text-neutral-300 font-bold mb-2">Dependência Parasita</strong><span className="text-neutral-500 font-light text-lg">Sem controle e transparência unificada. Você necessita do desenvolvedor só para arrumar erro ortográfico no seu site.</span></li>
                    </ul>
                 </div>
                 {/* COLUNA NYROH */}
                 <div className="p-8 md:p-16 bg-gradient-to-br from-purple-900/10 to-transparent">
                    <h3 className="text-2xl font-black uppercase tracking-widest text-purple-400 mb-10 flex items-center justify-between">O Modelo Nyroh <Check className="text-purple-400"/></h3>
                    <ul className="space-y-12">
                       <li><strong className="block text-xl text-white font-bold mb-2">A Matemática Extrativista da Eficiência</strong><span className="text-neutral-300 font-light text-lg">Valor limpo de agilidade. Nosso arsenal acelera a fabricação da arquitetura base. Só o trabalho valioso, limpo e enxuto compõe os honorários.</span></li>
                       <li><strong className="block text-xl text-white font-bold mb-2">Velocidade como Fator Primário</strong><span className="text-neutral-300 font-light text-lg">Ciclos inteiros convertidos em curtas fatias. Entregas liberadas por setups agressivos visando lucro desde a semana limite.</span></li>
                       <li><strong className="block text-xl text-white font-bold mb-2">Autonomia Integrada por Vias Tecnológicas</strong><span className="text-neutral-300 font-light text-lg">Tabelas de acompanhamento em Console e Edição Ao Vivo. O teto e as rédeas inibe-se sob o dono verdadeiro da empresa operadora.</span></li>
                    </ul>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* DOBRA 12: A Tese de Qualidade Suprema */}
      <section className="py-40 px-6 bg-[#030303] border-y border-white/5 relative overflow-hidden">
         <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-[200%] bg-gradient-to-b from-transparent via-purple-500/20 to-transparent rotate-45 pointer-events-none"></div>
         <div className="max-w-4xl mx-auto text-center relative z-10">
            <Blocks className="text-purple-500 mx-auto mb-10" size={64} strokeWidth={1} />
            <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-12 leading-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-neutral-500">
               Nós não confundimos atalho tecnológico com sucateamento do intelecto humano.
            </h2>
            <p className="text-2xl text-neutral-300 leading-relaxed font-light mb-8 pt-8">
               O valor da Nyroh é esmagadoramente mais limpo do mercado, mas nós abolimos a entrega genérica. Entregamos o primor técnico cobiçado globalmente porque as I.As e os frameworks da nossa espinha dorsal realizam a burocracia do chato e maçante.<br/><br/>
               <em className="text-purple-300 not-italic font-medium">A genialidade focada, de mente humana sênior em nossa estrutura, entra onde importa: na agressividade exata da sua estratégia criativa comercial e na beleza singular do que exala sua classe. Nós libertamos a equipe para arquitetar lucro e arte limpa para você.</em>
            </p>
         </div>
      </section>

      {/* DOBRA 13: Dinâmica Modular */}
      <section className="py-40 px-6 bg-black">
         <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-20">
            <div className="flex-1 w-full relative">
               <div className="aspect-square bg-[#050505] p-12 border border-white/5 rounded-full flex flex-col items-center justify-center text-center shadow-[inset_0_0_100px_rgba(255,255,255,0.02)]">
                  <div className="w-full flex items-center justify-between px-10 relative">
                     <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-white/10 border-dashed z-0"></div>
                     <span className="relative z-10 w-16 h-16 rounded-full bg-[#080808] border border-white/20 flex items-center justify-center"><LayoutTemplate className="text-purple-400" /></span>
                     <span className="relative z-10 w-16 h-16 rounded-full bg-black border border-white/10 flex items-center justify-center opacity-30"><Share2 /></span>
                     <span className="relative z-10 w-16 h-16 rounded-full bg-[#080808] border border-white/20 flex items-center justify-center"><Target className="text-emerald-400" /></span>
                  </div>
                  <h3 className="text-3xl font-black text-white mt-16 mb-4">Arquitetura Flexível</h3>
               </div>
            </div>
            <div className="flex-1">
               <h2 className="text-5xl lg:text-6xl font-black tracking-tighter mb-10">Você contrata a cura, não o excesso.</h2>
               <p className="text-2xl text-neutral-400 font-light leading-relaxed">
                  Operamos em modularidade agressiva. Você não é obrigado a fechar acordões que mesclam serviços inócuos ou pesados que sua operação sequer detém cacife.
                  Mapeamos os seus calcanhares de Aquiles. Falta vitrine veloz? Plugamos o time de Código. Já hospeda conversão mas leads congelam? Soltamos o tráfego Ads no topo ou o bot de atendimento invisível. O remédio certo desincha seu faturamento em 100%.
               </p>
            </div>
         </div>
      </section>

      {/* DOBRA 14: FAQ Escalonado */}
      <section className="py-40 px-6 bg-[#030303] border-y border-white/5">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-20">
           <div className="lg:w-1/3">
              <h2 className="text-5xl font-black mb-8 tracking-tighter text-white leading-tight">Aniquilando mitos covardes do mercado.</h2>
              <p className="text-2xl text-neutral-400 font-light leading-relaxed">A resposta fria e cirúrgica da verdade sem eufemismos.</p>
           </div>
           <div className="lg:w-2/3 flex flex-col mt-4">
              <FAQItem 
                question="A automatização Nyroh vai arruinar a identidade artesanal da minha marca?" 
                answer="Intimamente pelo contrário. As máquinas, o código e a I.A base de nossa arquitetura só empilham os 'tijolos sujos'. Operam métricas e infraestrutura sem alma. Essa via rápida e técnica, devolve o tempo exato à alma criativa verdadeira da Nyroh desenhar cada linha em requinte luxuoso visando perfeitamente encantar e converter os ímpetos de desejo de seu cliente superior."
              />
              <FAQItem 
                question="Se eu não gostei de meu projeto, estou acorrentado com senhas reféns?" 
                answer="Nunca. Abominamos o formato sequestro refém de agências fracas de retenção suja. O Console de Gerenciamento dispensa-nos. No remoto evento divergente final, a passagem inteira e livre é sua inquestionavelmente, nós não barramos o traçado original do legado seu já montado ou tracionado."
              />
              <FAQItem 
                question="E o diagnóstico e escopo, demoram ou sangram meses inteiros até a entrega inicial?" 
                answer="Sua reunião inicial define cirurgicamente nossa mira direta de atuação agressiva em horas. Como dissemos antes: As engenharias modernas matam semanas em fatias temporais puras e velozes. Exato momento o farol se vira pro mercado digital focado, operando conversões já calculadas incisivamente em parcos dias limite."
              />
           </div>
        </div>
      </section>

      {/* DOBRA 15: Personas Nulas (A Parede de Rejeição) */}
      <section className="py-40 px-6 bg-[#000000]">
        <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-5xl md:text-6xl font-black mb-16 tracking-tighter">O Limiar do Fechamento de Portas Nyroh.</h2>
            <div className="grid md:grid-cols-2 gap-12 bg-[#050505] p-12 md:p-20 rounded-[3rem] border border-white/5 relative shadow-xl overflow-hidden">
                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/5 hidden md:block"></div>
                <div className="text-left md:pr-12">
                   <h4 className="text-3xl font-bold mb-8 flex items-center gap-4 text-neutral-400"><X className="text-neutral-600" size={32}/> A Quem Rejeitamos:</h4>
                   <p className="text-neutral-500 leading-relaxed text-xl font-light">
                      O "caçador do barato ilusório", pedindo descontinhos pra escopos em migalhas. <br/><br/>
                      Empresários letárgicos operando zonas de extremo conforto ou planilhas inúteis manuais e satisfeitos com sangria vazia sem resultados milionários de vendas.
                   </p>
                </div>
                <div className="text-left md:pl-12 pt-12 md:pt-0 border-t border-white/5 md:border-0">
                   <h4 className="text-3xl font-bold mb-8 flex items-center gap-4 text-white"><Check className="text-purple-400" size={32}/> Aos Empresários Válidos:</h4>
                   <p className="text-neutral-300 leading-relaxed text-xl font-light">
                      Gestão traumatizada e farta da histórica falta de integridade corporativa, lentidão e faturamento rastejante cego. <br/><br/>
                      Cálices dispostos a absorverem a engrenagem arquitetônica total base dos Hubs digitais de tracionamentos imperativos e limpos por toda equipe nossa operante.
                   </p>
                </div>
            </div>
        </div>
      </section>

      {/* DOBRA 16 & 17: O Abismo da Decisão & CTA Brutal */}
      <section className="py-56 px-6 relative flex flex-col items-center justify-center bg-[#010101] min-h-screen">
        <div className="absolute inset-0">
           {/* Grid Matrix Pesado Fundo */}
           <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
           <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[80vw] w-[80vw] max-w-[1200px] rounded-full bg-purple-600/10 blur-[200px] mix-blend-screen pointer-events-none"></div>
        </div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10 flex flex-col items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once:true }} className="mb-24">
             <h2 className="text-5xl md:text-7xl font-black mb-12 leading-[1.1] tracking-tighter">
               Cada dia de hesitação num modelo engessado é lucro abdicado no caixa do seu concorrente livre.
             </h2>
             <p className="text-2xl md:text-3xl text-neutral-400 font-light leading-relaxed">
               Não suporte faturas ou atrasos injustificáveis corporativos mantendo obsoleto. O núcleo maciço estruturado do Nyroh aguarda as ordens pra impulsionamento físico irrevogável em sua conta.
             </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once:true }} transition={{ delay: 0.1 }}>
             <a href="https://wa.me/5511999999999" target="_blank" rel="noreferrer" className="relative group inline-block overflow-hidden rounded-[2rem] p-1">
               <span className="absolute inset-0 rounded-[2rem] bg-[image:linear-gradient(90deg,#9333ea,#ffffff,#4f46e5,#9333ea)] bg-[length:200%_auto] animate-[spin_4s_linear_infinite] opacity-60 group-hover:opacity-100 transition-opacity"></span>
               <div className="relative bg-[#000] rounded-[1.8rem] px-16 py-12 flex flex-col items-center justify-center transition-all group-hover:bg-[#030303] shadow-[inset_0_0_80px_rgba(168,85,247,0.1)]">
                  <span className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-purple-200 uppercase">
                     Reunião Mestra Inicial.
                  </span>
                  <span className="text-neutral-400 flex items-center gap-2 font-black tracking-[0.3em] uppercase text-sm mt-2">
                     <InfinityIcon size={18} className="text-purple-500"/> Auditoria Operacional Absoluta Nyroh <ArrowRight size={16} className="text-white group-hover:translate-x-2 transition-transform ml-2"/>
                  </span>
               </div>
             </a>
          </motion.div>
        </div>
      </section>

      {/* DOBRA 18: Lastro Oculto (Review / Prova Definitiva) */}
      <section className="py-32 border-t border-white/5 bg-[#020202] px-6 text-center text-xl font-light text-neutral-500 tracking-wide overflow-hidden relative">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-20 relative z-10 px-4">
             <div className="flex flex-col gap-8 items-center text-center">
                <Quote size={40} className="text-purple-500/20 rotate-180" />
                <p className="leading-relaxed">"Desintegraram num pulo seis meses arrastados por minha dita 'Agência Expert Premium'. Nossa retaguarda tecnológica convergiu instantânea logo nas quinzenas rasgadas finais com maestria cega aliada."</p>
             </div>
             <div className="flex flex-col gap-8 items-center text-center">
                <Quote size={40} className="text-purple-500/20 rotate-180" />
                <p className="leading-relaxed">"Surreal visualizando internamente tudo rodando Dashboard console ativo diário. Fica esmagador pra operação clássica tentar disfarçar números vazios ante minha posse unificada Nyroh completa tracionar de modo veloz limpo."</p>
             </div>
             <div className="flex flex-col gap-8 items-center text-center">
                <Quote size={40} className="text-purple-500/20 rotate-180" />
                <p className="leading-relaxed">"Extermínio nítido base injustiça econômica. Repasse exato financeiro gerando estrutura pesada e requinte classe que não cai orgânica se compararmos."</p>
             </div>
          </div>
      </section>

      {/* DOBRA 19: Rodapé Nyroh Hub */}
      <footer className="py-24 bg-[#000000] px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-16">
          
          <div className="flex flex-col items-center lg:items-start gap-6">
             <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                   <span className="text-black font-black text-2xl tracking-tighter">N</span>
                </div>
                <span className="font-black text-3xl tracking-widest uppercase text-white">Nyroh Core</span>
             </div>
             <p className="text-neutral-500 text-lg font-light text-center lg:text-left mt-2">
                O Hub Definitivo De Otimizações & Aceleração Operacional Autônoma. <br className="hidden md:block"/>
                <span className="text-neutral-600 italic">O impiedoso extermínio da ineficiência clássica corporativa final.</span>
             </p>
          </div>
          
          <div className="text-neutral-700 text-xs text-center font-mono tracking-widest uppercase">
            &copy; {new Date().getFullYear()} NYROH INFRA E TECHNOLOGIES LTDA. DIREITOS E AUTORIDADES BLINDADOS INCLUSOS.
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-8 justify-center mt-8 lg:mt-0">
             <a href="#" className="text-xs uppercase tracking-widest font-bold text-neutral-600 hover:text-white transition-colors">Normas e Políticas Privadas Base</a>
             <div className="h-6 w-[1px] bg-white/10 hidden sm:block"></div>
             {/* Acessos Secretos da Plataforma (Restaurado e Expandidos) */}
             <a href="#/login" className="px-8 py-3.5 bg-[#0A0A0A] border border-white/10 hover:border-white/30 rounded text-xs uppercase tracking-widest font-bold text-neutral-400 hover:text-white transition-colors shadow-2xl">Acesso Cliente Hub</a>
             <a href="#/core" className="px-8 py-3.5 bg-purple-900/10 border border-purple-500/20 hover:border-purple-500/60 rounded text-xs uppercase tracking-widest font-black text-purple-400 hover:text-purple-300 transition-colors shadow-2xl">Console Nativo Admin Engine</a>
          </div>

        </div>
      </footer>

    </div>
  );
};
